import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, ReportComposerService, ReportConfiguration, SVGUtils } from '@blk/simba';
import { ViewCategory } from '@ddt-web/config';
import { ConfigService, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, DisplayDataFactoryService, ViewDataFactoryService } from '@ddt-web/data-service';
import { ClientOutput, SharedApplicationConstants } from '@ddt-web/domain';
import { Replacer } from '@ddt-web/rules-engine';
import * as dayjs from 'dayjs';
import * as get from 'lodash/get';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, first, map, mergeMap, timeout } from 'rxjs/operators';

import { AchoEditModeDecorator } from '../utils/acho-edit-mode-decorator';
import { AchoReportConfiguration } from '../utils/acho-report-configuration';
import { ReportUtils } from '../utils/report-utils';

@Injectable({
	providedIn: 'root'
})
export class AchoReportComposerService extends ReportComposerService {
	private scenarioYieldSubject = new ReplaySubject<boolean>();
	public toggleScenarioYield$: Observable<boolean>;

	constructor(
		private viewDataSvc: ViewDataFactoryService,
		private displayDataFactory: DisplayDataFactoryService,
		private contentService: ContentService,
		private configService: ConfigService
	) {
		super();
		this.toggleScenarioYield$ = this.scenarioYieldSubject.asObservable();
	}

	public composeReport(
		reportConfig: AchoReportConfiguration,
		contentService: ContentService,
		clientOutput: ClientOutput,
		applicationState?: any,
		componentDataMapping$?: Observable<Map<string, ComponentData>>
	): Observable<string[]> {
		const filteredReportConfig = new AchoReportConfiguration(reportConfig.pages.filter(page => page.visible), reportConfig.viewCategory, reportConfig.pageTemplate);
		this.setupEditMode();

		if (!componentDataMapping$) {
			componentDataMapping$ = this.getComponentDataMapping(filteredReportConfig, contentService, clientOutput, reportConfig.componentReqIdMapping);
		}

		return componentDataMapping$.pipe(
			filter(mapping => {
				const dataReady = mapping && this.isDataReady(Array.from(mapping.values()));
				if (!dataReady) {
					console.warn(`${ReportComposerService.LOGGER_PREFIX} Report composer is still waiting for data`, mapping);
				} else {
					// Dispose of components once generation process is completed
					Array.from(mapping.keys()).forEach(key => {
						this.viewDataSvc.componentDisposed(key);
					});
				}
				return dataReady;
			}),
			timeout(SharedApplicationConstants.DEFAULT_REQUEST_TIMEOUT),
			first(),
			mergeMap(mapping => {
				return this.generateReport(filteredReportConfig, mapping, contentService, clientOutput, applicationState);
			})
		);
	}

	public getComponentDataMapping(
		reportConfig: AchoReportConfiguration,
		contentService: ContentService,
		clientOutput: ClientOutput,
		componentReqIdMapping?: { [id: string]: Observable<string[]> }
	): Observable<Map<string, ComponentData>> {
		const dataIds: string[] = this.collectComponentDataIds(reportConfig);
		dataIds.forEach(id => this.viewDataSvc.componentUsed(id));
		const componentDataObservables: Observable<ComponentData>[] = dataIds.map(id => {
			return this.displayDataFactory.getDisplayableComponentData(
				id,
				get(componentReqIdMapping, id, null),
				contentService,
				clientOutput,
				of(reportConfig.viewCategory)
			);
		});

		if (dataIds?.length) {
			return combineLatest([...componentDataObservables]).pipe(
				map(componentDataList => {
					return componentDataList.reduce((mapping, componentData, index) => {
						mapping.set(dataIds[index], componentData);
						return mapping;

					}, new Map<string, ComponentData>());
				})
			);
		}
		else {
			return of(new Map());
		}
	}

	public generateReport(
		reportConfig: AchoReportConfiguration,
		componentDataLookup: Map<string, ComponentData>,
		contentProvider: ContentService,
		clientOutput: ClientOutput,
		applicationState?: any
	): Observable<string[]> {
		const finalPageMarkup: string[] = [];
		// Init contentService global
		AbstractRenderer.contentProvider = contentProvider;

		try {
			reportConfig.sortPages();
			const pageTemplates = super.getPageTemplates(reportConfig, componentDataLookup, contentProvider, applicationState);
			const contentReplacer = new Replacer({});
			const enableEuropeanDateFormat = this.configService.getBoolean(SharedConfigKey.ENABLE_EUROPEAN_DATE, false);
			let asOfDate;
			if (enableEuropeanDateFormat) {
				asOfDate = dayjs().format('DD/MM/YYYY');
			} else {
				asOfDate = dayjs().format('MMMM D, YYYY');
			}

			let index: number;
			for (index = 0; index < pageTemplates.length; index++) {
				const pageSvg: string = pageTemplates[index];
				const substitutionMap: Object = {};
				substitutionMap['date'] = asOfDate;
				substitutionMap['pageNo'] = index + 1;
				substitutionMap['pageTotal'] = pageTemplates.length;
				contentReplacer.populateDCRTags(pageSvg, substitutionMap, contentProvider);

				const substitutionMapWithDcrDefaults = this.mergeDcrDefaultsWithSubstitutionMap(substitutionMap, this.getDcrDefaultsByViewCategory(reportConfig.viewCategory));
				const mapToEvaluate = { ...clientOutput, ...substitutionMapWithDcrDefaults };
				const evaluatedSvg = contentReplacer.resolveDataValues(pageSvg, mapToEvaluate, contentReplacer.TAG_REGEX, '$');
				const componentDoc: Document = new DOMParser().parseFromString(evaluatedSvg, 'text/html');
				finalPageMarkup.push(componentDoc.body.innerHTML.trim());
			}
		} catch (e) {
			console.error(`${ReportComposerService.LOGGER_PREFIX} Error encountered during report generation`, e);
			throw new Error('Report generation failed');
		}

		return of(finalPageMarkup);
	}

	public collectComponentDataIds(reportConfig: ReportConfiguration): string[] {
		const dataIds: string[] = [];

		if (!reportConfig || !reportConfig.pages || !reportConfig.pages.length) {
			return dataIds;
		}

		return Array.from(reportConfig.pages.reduce((componentIds, section) => {
			const idsForSection = section.getComponentIds();
			idsForSection.forEach(componentIds.add, componentIds);

			return componentIds;
		}, new Set<string>()));
	}

	/**
	 * Why you ask?
	 * Well it seems when zipping a bunch of observables returned from the data layer, an array populated with nulls is initially returned.
	 * Then at some point after that, the actual data turns up.
	 * @param arr an array that might be completely useless.
	 */
	public isDataReady(arr: ComponentData[]): boolean {
		let useless = true;
		if (!arr || !arr.length) { return useless; }
		useless = arr.findIndex(data => !data || data.isLoading) !== -1;
		return !useless;
	}

	private setupEditMode(): void {
		if (location.href.indexOf('showEditor=true') > -1) {
			SVGUtils.enableEditMode();
			SVGUtils.setEditModeDecorator(new AchoEditModeDecorator());
		}
	}

	public toggeleScenarioYield(includeScenarioYield: boolean): void {
		this.scenarioYieldSubject.next(includeScenarioYield);
	}

	private getDcrDefaultsByViewCategory(reportType: ViewCategory): Object {
		switch (reportType) {
			case ViewCategory.FA_SELF_SERVICE_PDF:
				return ReportUtils.getDcrDefaults([
					'DCR_PA_SELF_SERVICE_COVER_PAGE_DISCLOSURE',
					'DCR_PA_SELF_SERVICE_COVER_PAGE_FINANCIAL_PROFESSIONAL_DISCLOSURE',
					'DCR_PA_SELF_SERVICE_FOOTER_TEMPLATE_FINANCIAL_PROFESSIONAL_DISCLOSURE'
				]);
			case ViewCategory.CLIENT_PDF:
				return ReportUtils.getDcrDefaults(['DCR_PA_CLIENT_COVER_PAGE_DISCLOSURE']);
			case ViewCategory.PROPOSAL_PDF:
				return ReportUtils.getDcrDefaults(['DCR_PA_PROPOSAL_COVER_PAGE_DISCLOSURE']);
			case ViewCategory.FA_PLUS_PDF:
				return ReportUtils.getDcrDefaults([
					'DCR_PA_PS_COVER_PAGE_DISCLOSURE',
					'DCR_PA_PS_COVER_PAGE_FINANCIAL_PROFESSIONAL_DISCLOSURE',
					'DCR_PA_PS_FOOTER_FINANCIAL_PROFESSIONAL_DISCLOSURE'
				]);
			default:
				return new Object();

		}
	}

	private mergeDcrDefaultsWithSubstitutionMap(substitutionMap: Object, dcrDefaults: Object): Object {
		if (!dcrDefaults) {
			return { ...substitutionMap };
		}

		return Object.entries(substitutionMap).reduce((mergedObject, [key, val]) => {
			if (dcrDefaults[key] && key === val) { // key == val when contentService.get is passed key as the default value which we do above
				return { ...mergedObject, [key]: dcrDefaults[key] };
			}
			return { ...mergedObject, [key]: val };
		}, {});
	}

}
